import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Ban, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

/**
 * /admin/court-blocks — Court Availability Blocks admin.
 * Blocks a court/time window from being booked by ties_create,
 * matches_reschedule and the Auto-Schedule / Draw Generator.
 */
const fmt = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d) ? iso : d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
};

export default function CourtBlocksAdmin() {
  const [blocks, setBlocks] = useState([]);
  const [court, setCourt] = useState("Court 1");
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [startTime, setStartTime] = useState("17:30");
  const [endTime, setEndTime] = useState("20:30");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [recurring, setRecurring] = useState(false);
  const [days, setDays] = useState([]); // 0=Mon .. 6=Sun
  const [until, setUntil] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/court-blocks");
      setBlocks(data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!court.trim()) return toast.error("Court is required");
    if (recurring && days.length === 0) return toast.error("Pick at least one day of the week");
    setBusy(true);
    try {
      const body = { court: court.trim(), reason: reason.trim() || null };
      if (recurring) {
        body.recurrence = {
          frequency: "weekly",
          days_of_week: days,
          start_time: startTime,
          end_time: endTime,
          until: until || null,
        };
      } else {
        body.start_at = `${date}T${startTime}:00+00:00`;
        body.end_at = `${date}T${endTime}:00+00:00`;
      }
      await api.post("/court-blocks", body);
      toast.success(recurring ? `Recurring block created on ${court}` : `Blocked ${court} from ${startTime} to ${endTime}`);
      setReason("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const toggleDay = (d) => setDays((prev) => prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d].sort());

  const remove = async (id) => {
    if (!window.confirm("Remove this court block?")) return;
    try {
      await api.delete(`/court-blocks/${id}`);
      toast.success("Block removed");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="p-6 md:p-10 min-h-screen bg-[#060d09] text-white" data-testid="court-blocks-admin">
      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Season 3 · Ops</div>
        <h1 className="font-anton text-5xl md:text-6xl uppercase tracking-tighter">Court Availability Blocks</h1>
        <p className="text-sm text-zinc-400 mt-2 max-w-2xl">
          Mark courts unavailable for maintenance, sponsor events or private bookings. The scheduler,
          drag-drop grid and Create Draws wizard will reject any tie whose start time falls inside a block window.
        </p>
      </div>

      {/* Create block form */}
      <Card className="p-5 bg-[#0f1a15] border-white/10 mb-6" data-testid="court-block-form">
        <div className="flex items-center gap-2 mb-3">
          <Plus className="w-4 h-4 text-[#F4B400]" />
          <div className="font-anton text-xl uppercase tracking-tight">Add Block</div>
          <div className="ml-auto flex items-center gap-1 text-[10px] uppercase tracking-widest" data-testid="mode-toggle">
            <button
              onClick={() => setRecurring(false)}
              className={`px-2.5 py-1 rounded border ${!recurring ? "bg-white/10 border-white/30 text-white" : "border-white/10 text-zinc-500"}`}
              data-testid="mode-oneoff"
            >One-off</button>
            <button
              onClick={() => setRecurring(true)}
              className={`px-2.5 py-1 rounded border ${recurring ? "bg-[#F4B400]/20 border-[#F4B400]/40 text-[#F4B400]" : "border-white/10 text-zinc-500"}`}
              data-testid="mode-recurring"
            >Recurring</button>
          </div>
        </div>
        <div className="grid md:grid-cols-6 gap-2 items-end">
          <div>
            <label className="text-[10px] uppercase text-zinc-500">Court</label>
            <Input value={court} onChange={(e) => setCourt(e.target.value)} className="bg-black/40 border-white/10 text-white" data-testid="cb-court" />
          </div>
          {!recurring && (
            <div>
              <label className="text-[10px] uppercase text-zinc-500">Date</label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="bg-black/40 border-white/10 text-white" data-testid="cb-date" />
            </div>
          )}
          <div>
            <label className="text-[10px] uppercase text-zinc-500">Start</label>
            <Input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} className="bg-black/40 border-white/10 text-white" data-testid="cb-start" />
          </div>
          <div>
            <label className="text-[10px] uppercase text-zinc-500">End</label>
            <Input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} className="bg-black/40 border-white/10 text-white" data-testid="cb-end" />
          </div>
          {recurring && (
            <div>
              <label className="text-[10px] uppercase text-zinc-500">Until (optional)</label>
              <Input type="date" value={until} onChange={(e) => setUntil(e.target.value)} className="bg-black/40 border-white/10 text-white" data-testid="cb-until" />
            </div>
          )}
          <div className={recurring ? "md:col-span-1" : "md:col-span-2"}>
            <label className="text-[10px] uppercase text-zinc-500">Reason (optional)</label>
            <div className="flex gap-2">
              <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Maintenance / Sponsor event / …" className="bg-black/40 border-white/10 text-white flex-1" data-testid="cb-reason" />
              <Button onClick={create} disabled={busy} className="bg-[#F4B400] text-black font-bold hover:bg-[#e5aa00]" data-testid="cb-create-btn">
                <Ban className="w-4 h-4 mr-1" /> Block
              </Button>
            </div>
          </div>
        </div>
        {recurring && (
          <div className="mt-3">
            <label className="text-[10px] uppercase text-zinc-500 block mb-1">Days of Week</label>
            <div className="flex flex-wrap gap-1" data-testid="cb-dow-picker">
              {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d, i) => (
                <button
                  key={i}
                  onClick={() => toggleDay(i)}
                  data-testid={`cb-dow-${i}`}
                  className={`text-xs uppercase tracking-widest px-3 py-1.5 rounded border ${
                    days.includes(i) ? "bg-[#F4B400]/20 border-[#F4B400]/40 text-[#F4B400]" : "bg-black/30 border-white/10 text-zinc-400 hover:text-white"
                  }`}
                >
                  {d}
                </button>
              ))}
            </div>
          </div>
        )}
      </Card>

      {/* Existing blocks */}
      <Card className="p-5 bg-[#0f1a15] border-white/10">
        <div className="font-anton text-xl uppercase tracking-tight mb-3">Active Blocks ({blocks.length})</div>
        {blocks.length === 0 ? (
          <div className="text-sm text-zinc-500 italic py-4">No blocks. Every court is fully available.</div>
        ) : (
          <div className="space-y-2">
            {blocks.map((b) => {
              const isRec = !!b.recurrence;
              const dayNames = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
              return (
              <div key={b.id} className={`flex items-center gap-3 p-3 rounded-lg border ${isRec ? "border-[#F4B400]/40 bg-[#F4B400]/5" : "border-red-900/40 bg-red-500/5"}`} data-testid={`court-block-${b.id}`}>
                <Ban className={`w-4 h-4 ${isRec ? "text-[#F4B400]" : "text-red-400"} shrink-0`} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white font-medium flex items-center gap-2">
                    {b.court}
                    {isRec && <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-[#F4B400]/20 border border-[#F4B400]/40 text-[#F4B400]">Weekly</span>}
                  </div>
                  <div className="text-[11px] text-zinc-500">
                    {isRec
                      ? `${(b.recurrence.days_of_week || []).map((d) => dayNames[d]).join(", ")} · ${b.recurrence.start_time}–${b.recurrence.end_time}${b.recurrence.until ? ` · until ${b.recurrence.until}` : ""}${b.reason ? ` · ${b.reason}` : ""}`
                      : `${fmt(b.start_at)} → ${fmt(b.end_at)}${b.reason ? ` · ${b.reason}` : ""}`}
                  </div>
                </div>
                <Button variant="ghost" size="icon" onClick={() => remove(b.id)} className="text-red-400 hover:text-red-300" data-testid={`cb-delete-${b.id}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            );})}
          </div>
        )}
      </Card>
    </div>
  );
}
